import { KeyboardEvent, SyntheticEvent, useCallback } from 'react'
import { SerializedStyles } from '@emotion/react'
import ControlPoint from '@mui/icons-material/ControlPoint'
import FilterList from '@mui/icons-material/FilterList'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import MoreVert from '@mui/icons-material/MoreVert'

import IconButton, { ColorVariant } from '../buttons/IconButton/IconButton'
import { SecondaryButton } from '../buttons'

type MenuButtonOptions = {
  /** Accessible label applied to the menu button */
  ariaLabel?: string
  /** Optional styles to add to the icon button */
  buttonCss?: SerializedStyles
  /** Indicates if the tooltip is shown on hover */
  displayTooltip?: boolean
  /** Indicates if menu is expanded/collapsed */
  expanded: boolean
  /** Icon for button without a label */
  icon?: 'Arrow' | 'ControlPoint' | 'FilterList' | 'MoreVert'
  /** Icon color variant, defaults to Secondary */
  iconColorVariant?: 'Primary' | 'Secondary'
  /** Optional label for button. If not provided, will fallback to icon */
  label?: string
  /** Optional id of menu control */
  menuId?: string
  /** Event callback when Menu Item is clicked */
  onClick?: (event: SyntheticEvent<HTMLElement>) => void
  /** Event callback when Menu Item is key-pressed */
  onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void
  /** Optional tooltip for Icon */
  tooltip?: string
}

const MenuButton = ({
  ariaLabel,
  buttonCss,
  displayTooltip = true,
  expanded,
  icon = 'MoreVert',
  iconColorVariant = 'Secondary',
  label,
  menuId,
  onClick,
  onKeyDown,
  tooltip
}: MenuButtonOptions) => {
  const Icon = useCallback(() => {
    switch (icon) {
      case 'Arrow':
        return <KeyboardArrowDown />
      case 'ControlPoint':
        return <ControlPoint />
      case 'MoreVert':
        return <MoreVert />
      case 'FilterList':
        return <FilterList />
      default:
        return <MoreVert />
    }
  }, [icon])
  return label ? (
    <SecondaryButton
      ariaLabel={ariaLabel}
      ariaHasPopup
      ariaControls={expanded ? menuId : undefined}
      css={buttonCss}
      expanded={expanded}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {label}
    </SecondaryButton>
  ) : (
    <IconButton
      ariaLabel={ariaLabel || 'Menu'}
      ariaHasPopup
      ariaControls={expanded ? menuId : undefined}
      colorVariant={ColorVariant[iconColorVariant!]}
      css={buttonCss}
      displayTooltip={displayTooltip}
      expanded={expanded}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tooltip={tooltip}
    >
      <Icon />
    </IconButton>
  )
}

export default MenuButton
